import { CommonModule, DatePipe } from "@angular/common";
import { Component } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from "@angular/material/slide-toggle";
import { RouterLink } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { Editor, NgxEditorModule, Toolbar } from "ngx-editor";
import { firstValueFrom, Observable } from "rxjs";
import { AcuerdoMrbService } from "../../../acuerdo-mrb/acuerdo-mrb.service";
import { AppState } from "../../../app.state";
import { ConfirmDialogComponent } from "../../../confirm-dialog/confirm-dialog.component";
import {
  INTemaAcuerdo,
  IRNAcuerdo,
  IRNAcuerdoNotificacion,
  IRNAcuerdoNotiNuevoTema,
  nuevoAcuerdo,
} from "../../../interfaces/acuerdos";
import { ICatNuevoAcuerdoMrb } from "../../../interfaces/catalogos.";
import { LoadingService } from "../../../loading/loading.service";
import { AcuerdosService } from "../../../services/acuerdos/acuerdos.service";
import {
  guardarNuevoAcuerdoMRB,
  loadResponsablesSuccess,
  nuevoAcuerdoSuccess,
} from "../../../states/acuerdos-mrb/actions";
import { selectCatalogosNuevoAcuerdo } from "../../../states/catalogos/selector";
import { selectUsuario } from "../../../states/usuario/selector";
import { toolbarEditor } from "../../../utils";
import { stripHtml } from "../detalle-tema/dtoDetalleTema";
import { AcuerdosUtilsService } from "../utils";

@Component({
  selector: "app-nuevo-acuerdo",
  standalone: true,
  imports: [
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    //RouterLink,
    MatDatepickerModule,
    MatSlideToggleModule,
    NgxEditorModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: "es-MX" },
    provideNativeDateAdapter(),
    DatePipe,
  ],
  templateUrl: "./nuevo-acuerdo.component.html",
  styleUrl: "./nuevo-acuerdo.component.css",
})
export class NuevoAcuerdoComponent {
  public nuevoAcuerdo!: FormGroup;
  public catalogos$!: Observable<ICatNuevoAcuerdoMrb>;
  public editor!: Editor;
  public catCategorizacion: any[] = [];
  public toolbar: Toolbar = toolbarEditor;
  private readonly stateUser: any = this.store.pipe(select(selectUsuario));

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private datePipe: DatePipe,
    public acuerdosService: AcuerdosService,
    public loadingService: LoadingService,
    public dialog: MatDialog,
    private acuerdoMrbService: AcuerdoMrbService,
    private acuerdoUtils: AcuerdosUtilsService
  ) {}

  async ngOnInit() {
    this.editor = new Editor();
    this.catalogos$ = this.store.select(selectCatalogosNuevoAcuerdo).pipe();
    this.nuevoAcuerdo = this.fb.group({
      nombreTema: [
        "",
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(1000),
        ],
      ],

      descripcionTema_html: ["", [Validators.required]],
      banAgregarTema: [false, [Validators.required]],
      fechaAcuerdo: [new Date(), [Validators.required]],
      tipoTema: [null, [Validators.required]],
      esConfidencial: [false, [Validators.required]],
      id_categoria: ["", [Validators.required]],
      categorizacion: ["", [Validators.required]],
      analista: [null, [Validators.required]],
      supervisor: [null, [Validators.required]],
      coordinador: [null, [Validators.required]],
    });

    this.loadCategorizacion(false);
  }

  confidencialSelected(event: MatSlideToggleChange) {
    console.log("confidencialSelected", event.checked);
    this.loadCategorizacion(event.checked);
    this.esConfidencial(event.checked);
  }

  async loadCategorizacion(isConfidencial: boolean) {
    const categorizacion = await firstValueFrom(
      this.acuerdoMrbService.getCatalogo(
        this.acuerdoMrbService.cats.CATEGORIZACION
      )
    );

    let option = this.acuerdoUtils.getCategorizacioCombo(categorizacion);

    if (isConfidencial) {
      this.nuevoAcuerdo.get("categorizacion")?.setValue(null);
    } else {
      this.nuevoAcuerdo.get("categorizacion")?.setValue(null);
    }
    this.catCategorizacion = option;
  }

  esConfidencial(confidencial: boolean) {
    if (!confidencial) {
      this.nuevoAcuerdo.get("analista")?.setValidators([Validators.required]);
      this.nuevoAcuerdo.get("supervisor")?.setValidators([Validators.required]);
    } else {
      this.nuevoAcuerdo.get("analista")?.clearValidators();
      this.nuevoAcuerdo.get("analista")?.setErrors(null);
      this.nuevoAcuerdo.get("supervisor")?.clearValidators();
      this.nuevoAcuerdo.get("supervisor")?.setErrors(null);
    }

    this.nuevoAcuerdo.get("analista")?.updateValueAndValidity();
    this.nuevoAcuerdo.get("supervisor")?.updateValueAndValidity();
  }

  disabled(): boolean {
    const banAgregarTema = this.nuevoAcuerdo.get("banAgregarTema")?.value;
    const acuerdoSinTema =
      this.nuevoAcuerdo.get("fechaAcuerdo")?.invalid && !banAgregarTema;
    const acuerdoConTema = this.nuevoAcuerdo.invalid && banAgregarTema;
    return acuerdoSinTema || acuerdoConTema;
  }

  getContador(value: any) {
    if (!value || typeof value !== "string" || !stripHtml(value)) {
      this.nuevoAcuerdo.get("descripcionTema_html")?.setValue("");
      return 0;
    }
    const strippedValue = stripHtml(value);
    return strippedValue.length;
  }

  getCategoriaDescripcion(id: string, catCategoria: any[]): string {
    const categoria = catCategoria.find((item: any) => item.id === id);
    return categoria ? categoria.descripcion : "";
  }

  async guardarAcuerdo() {
    this.loadingService.show();
    const user = structuredClone(this.stateUser.state().usuario);
    const formAcuerdo = this.nuevoAcuerdo.getRawValue();
    const acuerdo: IRNAcuerdo = this.createAcuerdo(formAcuerdo);
    const notiNuevoTema: IRNAcuerdoNotiNuevoTema = this.createNotiNuevoTema(
      user,
      formAcuerdo
    );
    const notiAcuerdo: IRNAcuerdoNotificacion = this.createNotiAcuerdo(
      user,
      formAcuerdo
    );

    try {
      const acuerdoResp = await this.acuerdosService.agregarAcuerdoMrb(acuerdo);
      console.log("acuerdoResp", acuerdoResp);
      console.log("formAcuerdo", formAcuerdo);

      if (acuerdoResp.error || acuerdoResp.message&&acuerdoResp.message.error) {
        this.showModal("Alerta",acuerdoResp.message.msg);
        this.loadingService.hide();
        return;
      }
  
      if (!formAcuerdo.banAgregarTema) {
        this.showModal("Éxito","Acuerdo agregado correctamente");
        this.store.dispatch(
          guardarNuevoAcuerdoMRB({ acuerdo, notiAcuerdo, notiNuevoTema })
        );
        return;
      }

      if (formAcuerdo.banAgregarTema) {
        const promises = [];
        promises.push(
          this.acuerdosService.nuevoAcuerdoMrbNotificacion(notiAcuerdo)
        );
        promises.push(
          this.acuerdosService.nuevoTemaMrbNotificacion(notiNuevoTema)
        );

        const [notiAcuerdoRes, notiNuevoTemaRes] = await Promise.all(promises);
        if (
          !acuerdoResp.error ||
          (!notiAcuerdoRes.error && !notiNuevoTemaRes.error)
        ) {
          this.store.dispatch(
            guardarNuevoAcuerdoMRB({ acuerdo, notiAcuerdo, notiNuevoTema })
          );
          this.showModal("Éxito","Acuerdo agregado correctamente");
        }
      }
    } catch (error) {
      this.loadingService.hide();
    }
  }

  createAcuerdo(formAcuerdo: any): IRNAcuerdo {
    const fechaAcuerdo =
      this.datePipe.transform(formAcuerdo.fechaAcuerdo, "yyyy-MM-dd") ?? "";

    const nuevoAcuerdo: IRNAcuerdo = {
      tipoForma: 1,
      fechaAcuerdo,
      banAgregarTema: formAcuerdo.banAgregarTema,
    };

    if (formAcuerdo.banAgregarTema) {
      nuevoAcuerdo.temas = [this.createTema(formAcuerdo)];
    }

    return nuevoAcuerdo;
  }
  createNotiAcuerdo(user: any, formAcuerdo: any): IRNAcuerdoNotificacion {
    return {
      nombreUsuario: user.nombre,
      fechaAcuerdo: this.getFechaCompleta(formAcuerdo.fechaAcuerdo),
      id_categorizacion: formAcuerdo.categorizacion,
    };
  }

  createTema(formAcuerdo: any): INTemaAcuerdo {
    return {
      nombreTema: formAcuerdo.nombreTema,
      descripcionTema: formAcuerdo.descripcionTema_html,
      descripcionTema_html: formAcuerdo.descripcionTema_html,
      tipoTema: formAcuerdo.tipoTema,
      esConfidencial: formAcuerdo.esConfidencial,
      categorizacion: formAcuerdo.categorizacion,
      id_categoria: formAcuerdo.id_categoria,
      respAnalista: formAcuerdo.analista?.idFB,
      respSupervisor: formAcuerdo.supervisor?.idFB,
      respCoordinador: formAcuerdo.coordinador?.idFB,
      indiceTema: 1,
    };
  }

  createNotiNuevoTema(user: any, formAcuerdo: any): IRNAcuerdoNotiNuevoTema {
    return {
      nombreUsuario: user.nombre,
      fechaAcuerdo: this.getFechaCompleta(formAcuerdo.fechaAcuerdo),
      nombreTema: formAcuerdo.nombreTema,
      descripcionTema: formAcuerdo.descripcionTema_html,
      nombreAnalista: formAcuerdo.analista?.nombre,
      nombreSupervisor: formAcuerdo.supervisor?.nombre,
      nombreCoordinador: formAcuerdo.coordinador?.nombre,
      id_categorizacion: formAcuerdo.categorizacion,
    };
  }

  getFechaCompleta(fecha: Date): string {
    const fechaAcuerdo = this.datePipe.transform(fecha, "dd/MM/YYYY") || "";
    const minutos = this.datePipe.transform(new Date(), "hh:mm:ss a") || "";
    return `${fechaAcuerdo}, ${minutos} `; // "16/10/2024, 2:40:02 p.m."
  }

  cancelar() {
    this.store.dispatch(loadResponsablesSuccess({ nuevoAcuerdo }));
  }

  showModal(
    title: string,
    message: string
  ) {
   
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "450px",
      data: {
        title,
        message,
        showButtons: {
          accept: true,
          cancel: false,
        },
        direction: "end",
        buttonsName: { cancel: "CANCELAR", accept: "ACEPTAR" },
      },
      disableClose: true,
    });
  }
  ngOnDestroy(): void {
    this.editor.destroy();
  }
}
